/**
 * A hybrid map implementation that supports both object and primitive keys.
 * 
 * - Object keys are stored using a `WeakMap`, allowing for garbage collection when there are no other references.
 * - Primitive keys (`string`, `number`, `symbol`) are stored using a regular `Map`.
 * 
 * This class provides a unified API similar to `Map`, supporting `set`, `get`, `delete`, `has`, and `clear` methods.
 * 
 * @typeParam K - The type of keys, which can be an object, string, number, or symbol.
 * @typeParam V - The type of values stored in the map.
 * 
 * @example
 * ```typescript
 * const map = new HybridMap<object | string, number>();
 * const objKey = {};
 * map.set(objKey, 42);
 * map.set("foo", 7);
 * console.log(map.get(objKey)); // 42
 * console.log(map.get("foo")); // 7
 * ```
 */
export class HybridMap<K extends object | string | number | symbol, V> {
    private objectMap = new WeakMap<object, V>();
    private primitiveMap = new Map<string | number | symbol, V>();

    private isObjectKey(key: K): key is K & object {
        return typeof key === "object" && key !== null;
    }

    set(key: K, value: V): void {
        if (this.isObjectKey(key)) {
            this.objectMap.set(key, value);
        } else {
            this.primitiveMap.set(key as string | number | symbol, value);
        }
    }

    get(key: K): V | undefined {
        if (this.isObjectKey(key)) {
            return this.objectMap.get(key);
        } else {
            return this.primitiveMap.get(key as string | number | symbol);
        }
    }

    delete(key: K): boolean {
        if (this.isObjectKey(key)) {
            return this.objectMap.delete(key);
        } else {
            return this.primitiveMap.delete(key as string | number | symbol);
        }
    }

    has(key: K): boolean {
        if (this.isObjectKey(key)) {
            return this.objectMap.has(key);
        } else {
            return this.primitiveMap.has(key as string | number | symbol);
        }
    }

    clear(): void {
        this.primitiveMap.clear();
        this.objectMap = new WeakMap<object, V>(); // Reinitialize to clear WeakMap
    }
}